import React, { useState, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Book, Calendar, Star, Camera, Heart } from 'lucide-react';

const BookPreview = ({ 
  babyBook, 
  entries, 
  milestones, 
  dateRange, 
  contentFilter,
  format = 'hardcover' 
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const thumbnailsRef = useRef(null);

  // Filter entries by date range and content settings
  const filteredEntries = useMemo(() => {
    if (!entries) return [];
    
    return entries.filter(entry => {
      const entryDate = entry.timestamp?.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp);
      
      if (dateRange?.start && entryDate < new Date(dateRange.start)) return false;
      if (dateRange?.end && entryDate > new Date(dateRange.end)) return false;
      
      if (!contentFilter?.journalEntries && entry.text && !entry.milestone) return false;
      if (!contentFilter?.milestones && entry.milestone) return false;
      if (!contentFilter?.photos && entry.photos?.length > 0 && !entry.text) return false;
      
      return true;
    });
  }, [entries, dateRange, contentFilter]);

  const filteredMilestones = useMemo(() => {
    if (!milestones || !contentFilter?.milestones) return [];
    
    return milestones.filter(milestone => {
      const milestoneDate = milestone.date?.toDate ? milestone.date.toDate() : new Date(milestone.date);
      
      if (dateRange?.start && milestoneDate < new Date(dateRange.start)) return false;
      if (dateRange?.end && milestoneDate > new Date(dateRange.end)) return false;
      
      return true;
    });
  }, [milestones, dateRange, contentFilter]);

  // Generate book pages
  const pages = useMemo(() => {
    const bookPages = [];
    
    bookPages.push({
      type: 'cover',
      babyName: babyBook?.babyName || 'Baby',
      babyPhoto: babyBook?.babyPhoto,
      dateRange
    });

    bookPages.push({
      type: 'title',
      babyName: babyBook?.babyName || 'Baby',
      birthDate: babyBook?.birthDate,
      parents: {
        mom: babyBook?.momName,
        dad: babyBook?.dadName
      }
    });

    if (contentFilter?.preBirth && babyBook) {
      const preBirthContent = [];
      if (babyBook.howTheyMet) preBirthContent.push({ label: 'How We Met', text: babyBook.howTheyMet });
      if (babyBook.howFoundOut) preBirthContent.push({ label: 'Finding Out', text: babyBook.howFoundOut });
      if (babyBook.nameStory) preBirthContent.push({ label: 'Your Name Story', text: babyBook.nameStory });
      if (babyBook.pregnancyNotes) preBirthContent.push({ label: 'Pregnancy Journey', text: babyBook.pregnancyNotes });
      
      if (preBirthContent.length > 0) {
        bookPages.push({
          type: 'preBirth',
          title: 'Before You Arrived',
          content: preBirthContent
        });
      }
    }

    if (contentFilter?.birthDetails && babyBook?.birthDate) {
      bookPages.push({
        type: 'birth',
        babyName: babyBook?.babyName,
        birthDate: babyBook?.birthDate,
        birthTime: babyBook?.birthTime,
        weight: babyBook?.weight,
        length: babyBook?.length,
        hospital: babyBook?.hospital,
        doctor: babyBook?.doctorName
      });
    }

    const entriesByMonth = {};
    filteredEntries.forEach(entry => {
      const entryDate = entry.timestamp?.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp);
      const monthKey = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}`;
      if (!entriesByMonth[monthKey]) {
        entriesByMonth[monthKey] = [];
      }
      entriesByMonth[monthKey].push(entry);
    });

    Object.keys(entriesByMonth).sort().forEach(monthKey => {
      const monthEntries = entriesByMonth[monthKey];
      const [year, month] = monthKey.split('-');
      const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      bookPages.push({
        type: 'monthDivider',
        month: monthName,
        entryCount: monthEntries.length,
        photoCount: monthEntries.reduce((sum, e) => sum + (e.photos?.length || 0), 0)
      });

      let pageEntries = [];
      monthEntries.forEach(entry => {
        pageEntries.push(entry);
        
        const hasPhotos = pageEntries.some(e => e.photos?.length > 0);
        const maxEntries = hasPhotos ? 2 : 3;
        
        if (pageEntries.length >= maxEntries) {
          bookPages.push({
            type: 'entries',
            entries: [...pageEntries]
          });
          pageEntries = [];
        }
      });
      
      if (pageEntries.length > 0) {
        bookPages.push({
          type: 'entries',
          entries: pageEntries
        });
      }
    });

    if (filteredMilestones.length > 0) {
      bookPages.push({
        type: 'milestones',
        title: 'Milestones',
        milestones: filteredMilestones
      });
    }

    bookPages.push({
      type: 'backCover',
      babyName: babyBook?.babyName
    });

    return bookPages;
  }, [babyBook, filteredEntries, filteredMilestones, dateRange, contentFilter]);

  const goToPage = (index) => {
    if (index >= 0 && index < pages.length) {
      setCurrentPage(index);
      // Scroll thumbnail into view
      setTimeout(() => {
        const thumbnail = thumbnailsRef.current?.children[index];
        if (thumbnail) {
          thumbnail.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }, 50);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (time) => {
    if (!time) return '';
    return time;
  };

  const renderPage = (page) => {
    switch (page.type) {
      case 'cover':
        return (
          <div className="h-full flex flex-col items-center justify-center bg-gradient-to-br from-ocean-100 via-peach-50 to-ocean-100 p-4 sm:p-8 text-center">
            {page.babyPhoto ? (
              <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-white shadow-lg mb-4 sm:mb-6">
                <img src={page.babyPhoto} alt={page.babyName} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-full bg-ocean-200 flex items-center justify-center border-4 border-white shadow-lg mb-4 sm:mb-6">
                <Heart className="w-8 h-8 sm:w-12 sm:h-12 text-ocean-600" />
              </div>
            )}
            <h1 className="text-xl sm:text-3xl font-serif font-bold text-ocean-900 mb-1 sm:mb-2">
              {page.babyName}'s
            </h1>
            <h2 className="text-lg sm:text-2xl font-serif text-ocean-800 mb-2 sm:mb-4">
              Memory Book
            </h2>
            {page.dateRange?.start && page.dateRange?.end && (
              <p className="text-ocean-700 text-xs sm:text-sm">
                {formatDate(page.dateRange.start)} — {formatDate(page.dateRange.end)}
              </p>
            )}
          </div>
        );

      case 'title':
        return (
          <div className="h-full flex flex-col items-center justify-center bg-white p-4 sm:p-8 text-center">
            <Book className="w-8 h-8 sm:w-12 sm:h-12 text-ocean-400 mb-4 sm:mb-6" />
            <h1 className="text-xl sm:text-2xl font-serif font-bold text-ocean-900 mb-2 sm:mb-4">
              {page.babyName}
            </h1>
            {page.birthDate && (
              <p className="text-ocean-700 text-sm mb-4 sm:mb-6">
                Born {formatDate(page.birthDate)}
              </p>
            )}
            <div className="w-12 sm:w-16 h-0.5 bg-ocean-300 mb-4 sm:mb-6" />
            {(page.parents?.mom || page.parents?.dad) && (
              <p className="text-ocean-600 text-xs sm:text-sm italic">
                With love from {[page.parents?.mom, page.parents?.dad].filter(Boolean).join(' & ')}
              </p>
            )}
          </div>
        );

      case 'preBirth':
        return (
          <div className="h-full bg-ocean-50 p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-serif font-bold text-ocean-900 mb-3 sm:mb-4 text-center">
              {page.title}
            </h2>
            <div className="space-y-3 sm:space-y-4">
              {page.content.map((item, idx) => (
                <div key={idx}>
                  <h3 className="text-xs sm:text-sm font-semibold text-ocean-800 mb-1">{item.label}</h3>
                  <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'birth':
        return (
          <div className="h-full bg-gradient-to-b from-sand-50 to-white p-4 sm:p-6 flex flex-col items-center justify-center text-center">
            <Star className="w-8 h-8 sm:w-10 sm:h-10 text-ocean-400 mb-2 sm:mb-4" />
            <h2 className="text-lg sm:text-xl font-serif font-bold text-ocean-900 mb-1 sm:mb-2">
              Welcome to the World
            </h2>
            <h3 className="text-base sm:text-lg text-ocean-800 mb-2 sm:mb-4">{page.babyName}</h3>
            
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 w-full max-w-xs">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                {page.birthDate && (
                  <div>
                    <p className="text-gray-500 text-xs">Born</p>
                    <p className="font-semibold text-gray-800">{formatDate(page.birthDate)}</p>
                  </div>
                )}
                {page.birthTime && (
                  <div>
                    <p className="text-gray-500 text-xs">Time</p>
                    <p className="font-semibold text-gray-800">{formatTime(page.birthTime)}</p>
                  </div>
                )}
                {page.weight && (
                  <div>
                    <p className="text-gray-500 text-xs">Weight</p>
                    <p className="font-semibold text-gray-800">{page.weight}</p>
                  </div>
                )}
                {page.length && (
                  <div>
                    <p className="text-gray-500 text-xs">Length</p>
                    <p className="font-semibold text-gray-800">{page.length}</p>
                  </div>
                )}
              </div>
              {page.hospital && (
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t text-xs text-gray-600">
                  {page.hospital}
                </div>
              )}
            </div>
          </div>
        );

      case 'monthDivider':
        return (
          <div className="h-full bg-gradient-to-br from-ocean-100 to-peach-50 flex flex-col items-center justify-center p-4 sm:p-6 text-center">
            <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-ocean-500 mb-2 sm:mb-4" />
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-ocean-900">
              {page.month}
            </h2>
            <div className="flex gap-3 sm:gap-4 mt-2 sm:mt-4 text-xs sm:text-sm text-ocean-700">
              <span>{page.entryCount} entries</span>
              <span>•</span>
              <span>{page.photoCount} photos</span>
            </div>
          </div>
        );

      case 'entries':
        return (
          <div className="h-full bg-white p-3 sm:p-4 overflow-y-auto">
            <div className="space-y-3 sm:space-y-4">
              {page.entries.map((entry, idx) => {
                const entryDate = entry.timestamp?.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp);
                return (
                  <div key={idx} className="border-b border-gray-100 pb-2 sm:pb-3 last:border-0">
                    <p className="text-xs text-ocean-600 font-medium mb-1">
                      {entryDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                    
                    {entry.photos?.length > 0 && (
                      <div className={`grid gap-1 mb-2 ${entry.photos.length === 1 ? 'grid-cols-1' : entry.photos.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                        {entry.photos.slice(0, 6).map((photo, pIdx) => (
                          <div key={pIdx} className="aspect-square rounded overflow-hidden bg-gray-100">
                            <img src={photo} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {entry.text && (
                      <p className="text-gray-700 text-xs sm:text-sm leading-relaxed line-clamp-3">{entry.text}</p>
                    )}
                    
                    {entry.milestone && (
                      <div className="mt-1 sm:mt-2 bg-ocean-50 rounded px-2 py-1 inline-block">
                        <span className="text-xs font-semibold text-ocean-800">
                          ⭐ {entry.milestone}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'milestones':
        return (
          <div className="h-full bg-ocean-50 p-4 sm:p-6 overflow-y-auto">
            <h2 className="text-lg sm:text-xl font-serif font-bold text-ocean-900 mb-3 sm:mb-4 text-center">
              {page.title}
            </h2>
            <div className="space-y-2">
              {page.milestones.map((milestone, idx) => {
                const milestoneDate = milestone.date?.toDate ? milestone.date.toDate() : new Date(milestone.date);
                return (
                  <div key={idx} className="flex items-center gap-2 sm:gap-3 bg-white rounded-lg p-2 shadow-sm">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-ocean-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="w-3 h-3 sm:w-4 sm:h-4 text-ocean-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-ocean-900 text-xs sm:text-sm truncate">{milestone.type}</p>
                      <p className="text-xs text-gray-600">{formatDate(milestoneDate)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'backCover':
        return (
          <div className="h-full bg-gradient-to-br from-ocean-100 via-peach-50 to-ocean-100 flex flex-col items-center justify-center p-4 sm:p-8 text-center">
            <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-ocean-400 mb-2 sm:mb-4" />
            <p className="text-ocean-800 font-serif italic text-sm sm:text-base">
              Made with love for {page.babyName}
            </p>
            <p className="text-ocean-600 text-xs mt-2 sm:mt-4">
              Created with BabyBookin
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      {/* Book container - responsive sizing */}
      <div 
        className={`relative bg-white shadow-2xl ${
          format === 'hardcover' 
            ? 'ring-4 ring-ocean-800 rounded-sm' 
            : 'rounded-lg border border-gray-200'
        }`}
        style={{ 
          width: 'min(280px, 90vw)', 
          height: 'min(360px, 70vh)',
          minWidth: '200px',
          minHeight: '260px'
        }}
      >
        <div className="w-full h-full overflow-hidden rounded-sm">
          {renderPage(pages[currentPage])}
        </div>
        
        {format === 'hardcover' && (
          <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-r from-ocean-900 to-ocean-800 rounded-l-sm" />
        )}
      </div>

      {/* Navigation arrows + page info */}
      <div className="flex items-center gap-2 sm:gap-4 mt-4 sm:mt-6">
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 0}
          className="p-2 sm:p-3 rounded-full bg-ocean-100 text-ocean-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-ocean-200 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        
        <div className="text-center min-w-[80px] sm:min-w-[100px]">
          <p className="text-sm sm:text-base text-gray-700 font-medium">
            Page {currentPage + 1} of {pages.length}
          </p>
          <p className="text-xs text-gray-400 capitalize">
            {pages[currentPage]?.type.replace(/([A-Z])/g, ' $1').trim()}
          </p>
        </div>
        
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === pages.length - 1}
          className="p-2 sm:p-3 rounded-full bg-ocean-100 text-ocean-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-ocean-200 transition-colors"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      {/* Page thumbnails - scrollable container */}
      <div className="w-full mt-3 sm:mt-4 px-2">
        <div 
          ref={thumbnailsRef}
          className="flex gap-1 sm:gap-2 overflow-x-auto pb-2 scroll-smooth"
          style={{ 
            scrollbarWidth: 'thin',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {pages.map((page, idx) => (
            <button
              key={idx}
              onClick={() => goToPage(idx)}
              className={`flex-shrink-0 w-8 h-10 sm:w-10 sm:h-12 rounded border-2 flex items-center justify-center transition-all ${
                currentPage === idx 
                  ? 'border-ocean-500 bg-ocean-100 shadow-md' 
                  : 'border-gray-200 bg-gray-50 hover:border-ocean-300 hover:bg-ocean-50'
              }`}
            >
              <span className="text-xs text-gray-600 font-medium">{idx + 1}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-3 sm:mt-4 flex gap-4 sm:gap-6 text-center text-xs sm:text-sm text-gray-600">
        <div>
          <p className="font-semibold text-ocean-900">{pages.length}</p>
          <p className="text-xs">Pages</p>
        </div>
        <div>
          <p className="font-semibold text-ocean-900">{filteredEntries.length}</p>
          <p className="text-xs">Entries</p>
        </div>
        <div>
          <p className="font-semibold text-ocean-900">
            {filteredEntries.reduce((sum, e) => sum + (e.photos?.length || 0), 0)}
          </p>
          <p className="text-xs">Photos</p>
        </div>
        <div>
          <p className="font-semibold text-ocean-900">{filteredMilestones.length}</p>
          <p className="text-xs">Milestones</p>
        </div>
      </div>
    </div>
  );
};

export default BookPreview;
